// InvoiceBlock — блок «Счета» в карточке сделки (V14 W6 + V17 W1).
// Сводка (Итого/Оплачено/Остаток — Σ по счетам в валюте сделки, OQ-12) по ВСЕМ счетам
// сделки (FE-агрегация по странице size=100 — §3.6; при >100 счетов — «первые 100»,
// серверный агрегат — V14.2/OQ-12) + таблица счетов ≤5 + кнопки: «+ Счёт»
// (InvoiceNewDialog с preset сделки), «Создать платёж» (PaymentNewDialog
// с presetInvoice — «Оплатить счёт», W7 §3.8: поле «Счёт №INV-…», POST с invoiceId)
// и «Показать все →» (/invoices?dealId=). V17 W1: DRAFT — «Выставить»/«Редактировать».
// V17 W2: полоска сводки — InvoiceSummaryStrip / computeSummary из InvoiceDealSummary.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { InvoiceNewDialog } from '@/pages/invoices/components/InvoiceNewDialog';
import { InvoiceEditDialog } from '@/pages/invoices/components/InvoiceEditDialog';
import { InvoiceStatusChip } from '@/pages/invoices/components/InvoiceStatusChip';
import { InvoiceEmailDialog } from '@/pages/invoices/components/InvoiceEmailDialog';
import { PaymentNewDialog } from '@/pages/payments/components/PaymentNewDialog';
import { formatDate, formatInvoiceNumber, formatMoney } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';
import {
  INVOICE_SUMMARY_PAGE_SIZE,
  InvoiceSummaryStrip,
  computeSummary,
} from './InvoiceDealSummary';

/** Сколько строк счетов показываем в таблице блока (канон §3.8: ≤5 + «Показать все»). */
const BLOCK_PAGE_SIZE = 5;

interface InvoiceBlockProps {
  dealId: string;
  /** Валюта сделки — сводка считается по счетам этой валюты (OQ-12). */
  dealCurrency?: string;
}

/**
 * Блок «Счета» карточки сделки: сводка (size=100) + до 5 строк.
 * DRAFT — Выставить/Редактировать; OPEN — письмо и платёж; VOID — без действий.
 */
export function InvoiceBlock({ dealId, dealCurrency }: InvoiceBlockProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  // V15 W7 (§3.8): «Отправить письмо» — только для OPEN-счёта (правило §5 п.4).
  const [sendInvoice, setSendInvoice] = useState<Invoice | null>(null);
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /** Выставить DRAFT→OPEN — копия lifecycleMutation списка (PATCH + invalidate `['invoices']` без exact). */
  const issueMutation = useMutation({
    mutationFn: (invoice: Invoice) =>
      invoicesApi.update(invoice.id, { status: 'OPEN', expectedUpdatedAt: invoice.updatedAt }),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => {
      setActionError(extractApiErrorMessage(error, t('invoices.edit.error')));
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  const query = useQuery({
    queryKey: ['invoices', 'deal', dealId],
    queryFn: () => invoicesApi.list({ dealId, page: 0, size: INVOICE_SUMMARY_PAGE_SIZE }),
    enabled: Boolean(dealId),
  });

  const invoices = query.data?.content ?? [];
  // Сводка — по ВСЕМ счетам сделки из запроса (size=100), таблица — только первые 5.
  const summary = computeSummary(invoices, dealCurrency);
  const tableInvoices = invoices.slice(0, BLOCK_PAGE_SIZE);

  /** Кнопки строки: DRAFT — выставить/редактировать; OPEN — письмо/платёж; иначе пусто. */
  function rowActions(invoice: Invoice): JSX.Element | null {
    if (invoice.lifecycle === 'DRAFT') {
      return (
        <>
          <Button
            size="small"
            aria-label={t('invoices.issue')}
            disabled={issueMutation.isPending}
            onClick={() => issueMutation.mutate(invoice)}
          >
            {t('invoices.issue')}
          </Button>
          <Button size="small" aria-label={t('invoices.editAction')} onClick={() => setEditInvoice(invoice)}>
            {t('invoices.editAction')}
          </Button>
        </>
      );
    }
    if (invoice.lifecycle === 'OPEN') {
      return (
        <>
          <Button size="small" aria-label={t('email.send.title')} onClick={() => setSendInvoice(invoice)}>
            {t('email.send.title')}
          </Button>
          <Button size="small" aria-label={t('invoices.block.createPayment')} onClick={() => setPayInvoice(invoice)}>
            {t('invoices.block.createPayment')}
          </Button>
        </>
      );
    }
    return null;
  }

  return (
    <section aria-label={t('invoices.block.title')}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('invoices.block.title')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {t('invoices.block.add')}
        </Button>
      </Stack>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      ) : null}

      {query.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {query.error instanceof Error ? query.error.message : t('invoices.loadError')}
        </Alert>
      ) : null}

      {query.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('invoices.loading')}
        </Typography>
      ) : !query.data || query.data.content.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('invoices.empty')}
        </Typography>
      ) : (
        <>
          <InvoiceSummaryStrip summary={summary} currency={dealCurrency} />
          <TableContainer>
            <Table size="small" aria-label={t('invoices.block.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>{t('invoices.amount')}</TableCell>
                  <TableCell>{t('invoices.dueDate')}</TableCell>
                  <TableCell>{t('invoices.status')}</TableCell>
                  <TableCell align="right">{t('invoices.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{formatInvoiceNumber(invoice.invoiceNumber, invoice.invoiceDate)}</TableCell>
                    <TableCell>{formatMoney(invoice.amount, invoice.currency)}</TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">{rowActions(invoice)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {query.data && query.data.total > BLOCK_PAGE_SIZE ? (
        <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/invoices?dealId=${dealId}`)}>
          {t('invoices.block.showAll')}
        </Button>
      ) : null}

      <InvoiceNewDialog open={createOpen} onClose={() => setCreateOpen(false)} presetDealId={dealId} />
      {editInvoice ? (
        <InvoiceEditDialog open onClose={() => setEditInvoice(null)} invoice={editInvoice} />
      ) : null}
      <PaymentNewDialog open={Boolean(payInvoice)} onClose={() => setPayInvoice(null)} presetInvoice={payInvoice} />
      <InvoiceEmailDialog open={Boolean(sendInvoice)} onClose={() => setSendInvoice(null)} invoice={sendInvoice!} />
    </section>
  );
}
